// PDMentorShared.jsx
// Shared engine for PDParent and PDObserver.
// Pass a `config` prop to customise labels, colors, userType and API fields.
//
// Usage:
//   <PDMentorShared config={PARENT_CONFIG} />
//   <PDMentorShared config={MENTOR_CONFIG} />

import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import useFetch from "../../../hooks/useFetch";
import SwipeCard from "../modules/streaks/SwipeCard";
import StreakSettingsForm from "../modules/streaks/StreakSettingsForm"

// ─── Config shapes (defined in wrapper files, shown here for reference) ────────
//
// PARENT_CONFIG = {
//   userType:       "Parent",
//   memberLabel:    "Children",        // plural
//   memberSingular: "Child",           // singular
//   memberIcon:     "👧",
//   headerIcon:     "👨‍👧",
//   headerTitle:    "Parental Engagement",
//   headerSub:      "Guide your children's 12-week streak journey",
//   accentColor:    "pink",            // Tailwind color name
//   targetType:     "Child",           // stored in PDStreakSettings.targetType
// }
//
// MENTOR_CONFIG = {
//   userType:       "Mentor",
//   memberLabel:    "Mentees",
//   memberSingular: "Mentee",
//   memberIcon:     "🧑‍🎓",
//   headerIcon:     "🧑‍🏫",
//   headerTitle:    "Mentor Engagement",
//   headerSub:      "Guide your mentees' 12-week streak journey",
//   accentColor:    "cyan",
//   targetType:     "Mentee",
// }

// ─── Constants ────────────────────────────────────────────────────────────────

export const REWARD_TYPES = [
  { id: "Purplon", label: "Purplon", icon: "🟣" },
  { id: "DOORI",   label: "DOORI",   icon: "🎁" },
  { id: "Cash",    label: "Cash",    icon: "💰" },
  { id: "Custom",  label: "Custom",  icon: "✨" },
  { id: "None",    label: "None",    icon: "—"  },
];

export const WEEKDAYS = [
  "Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"
];

const TABS = (cfg) => [
  { id: "members",   icon: cfg.memberIcon, label: cfg.memberLabel  },
  { id: "planner",   icon: "📅",           label: "Planner"        },
  { id: "approvals", icon: "✅",           label: "Approvals"      },
  { id: "progress",  icon: "📊",           label: "Progress"       },
  { id: "rewards",   icon: "🎁",           label: "Rewards"        },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function isSubActive(sub) {
  return sub && sub.isActive && new Date(sub.endDate) >= new Date();
}

export function addDays(dateStr, n) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  d.setDate(d.getDate() + n - 1);
  return d.toISOString().split("T")[0];
}

export function computeWeekDates(startDate) {
  if (!startDate) return [];
  const base = new Date(startDate);
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(base);
    d.setDate(base.getDate() + i * 7);
    return d.toISOString().split("T")[0];
  });
}

export function getAvatar(name = "") {
  const avatars = ["🧒", "👧", "👦", "🧑", "👶"];
  return avatars[(name.charCodeAt(0) || 0) % avatars.length];
}

export const emptySlot = (name = "") => ({
  streakName: name, streakRemarks: "", rewardType: "None",
  rewardValue: "", rewardDescription: "", dooriEnabled: false,
});

export const defaultWeekRewards = () =>
  Array.from({ length: 12 }, (_, i) => emptySlot(`Week ${i + 1} Streak`));

// ─── Shared CSS ───────────────────────────────────────────────────────────────

export const inputCls = "w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-pink-500/50 transition-colors";
export const labelCls = "text-[11px] text-gray-400 font-medium mb-1 block";

// ─── StatPill ─────────────────────────────────────────────────────────────────

export const StatPill = ({ icon, value, label, color = "text-white" }) => (
  <div className="bg-gray-800/60 border border-gray-700/40 rounded-xl p-3 text-center">
    <div className="text-lg mb-0.5">{icon}</div>
    <div className={`font-black text-lg leading-none ${color}`}>{value}</div>
    <div className="text-[9px] text-gray-400 mt-0.5">{label}</div>
  </div>
);

// ─── ProgressBar ──────────────────────────────────────────────────────────────

export const ProgressBar = ({ current, target, color = "bg-cyan-500" }) => {
  const pct = Math.min(100, Math.round((current / Math.max(target, 1)) * 100));
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px] text-gray-400">
        <span>{current} / {target} weeks</span>
        <span className="font-bold text-white">{pct}%</span>
      </div>
      <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className={`h-full rounded-full ${color}`}
        />
      </div>
    </div>
  );
};

// ─── RewardSlotEditor ─────────────────────────────────────────────────────────

export const RewardSlotEditor = ({ slot, onChange, showDoori = false }) => {
  const set = (key, val) => onChange({ ...slot, [key]: val });
  return (
    <div className="space-y-3">
      <div>
        <label className={labelCls}>Streak Name</label>
        <input value={slot.streakName} onChange={e => set("streakName", e.target.value)}
          placeholder="e.g. Reading Habit" className={inputCls} />
      </div>
      <div>
        <label className={labelCls}>Remarks / Motivation Message</label>
        <textarea value={slot.streakRemarks} onChange={e => set("streakRemarks", e.target.value)}
          placeholder="Why this matters…" style={{height: "50px"}}
          className={inputCls + " resize-none"} />
      </div>
      <div>
        <label className={labelCls}>Reward Type</label>
        <div className="flex flex-row gap-1.5">
          {REWARD_TYPES.map(r => (
            <button key={r.id} onClick={() => set("rewardType", r.id)}
              className={`w-fit px-2 py-1 rounded-lg text-center border transition-all ${
                slot.rewardType === r.id
                  ? "bg-pink-500 border-pink-500 text-black"
                  : "bg-gray-800 border-gray-700 text-gray-200"}`}>
              <div className="text-base">{r.icon}</div>
              <div className="text-[10px] lg:text-xs mt-0.5 font-bold">{r.label}</div>
            </button>
          ))}
        </div>
      </div>
      {slot.rewardType !== "None" && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Value / Amount</label>
            <input type="number" value={slot.rewardValue}
              onChange={e => set("rewardValue", e.target.value)}
              placeholder="0" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <input value={slot.rewardDescription}
              onChange={e => set("rewardDescription", e.target.value)}
              placeholder="e.g. Ice cream outing" className={inputCls} />
          </div>
        </div>
      )}
      {showDoori && (
        <button onClick={() => set("dooriEnabled", !slot.dooriEnabled)}
          className={`w-full flex items-center justify-between p-4 rounded-2xl border-2 transition-all ${
            slot.dooriEnabled
              ? "border-cyan-500/60 bg-cyan-950/20"
              : "border-gray-700/60 bg-gray-900/60"}`}>
          <div className="text-left flex-1 min-w-0 pr-3">
            <div className={`text-sm font-bold ${slot.dooriEnabled ? "text-cyan-300" : "text-white"}`}>
              🏆 Associate DOORI Collectible?
            </div>
            <div className="text-[10px] text-gray-400 mt-0.5">
              Physical collectible dispatched on completing the full 12-week streak.
            </div>
          </div>
          <div className={`w-12 h-6 rounded-full border-2 flex items-center flex-shrink-0 transition-all ${
            slot.dooriEnabled ? "border-cyan-500 bg-cyan-900" : "border-gray-600 bg-gray-800"}`}>
            <motion.div animate={{ x: slot.dooriEnabled ? 24 : 2 }}
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
              className="w-4 h-4 rounded-full bg-white shadow" />
          </div>
        </button>
      )}
    </div>
  );
};

// ─── WeekPanel ────────────────────────────────────────────────────────────────

export const WeekPanel = ({ weekIndex, slot, onChange, targetDate, isFirstWeek }) => {
  const [open, setOpen] = useState(weekIndex === 0);
  return (
    <div className={`rounded-2xl border overflow-hidden transition-all ${
      open ? "border-pink-700/50 bg-pink-950/10" : "border-gray-700/50 bg-gray-900/60"}`}>
      <button onClick={() => setOpen(p => !p)}
        className="w-full flex items-center justify-between p-3">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-xs font-black flex-shrink-0 ${
            open ? "bg-pink-500 text-black" : "bg-gray-800 text-gray-300"}`}>
            W{weekIndex + 1}
          </div>
          <div className="text-left min-w-0">
            <div className="text-sm font-bold text-white truncate">
              {slot.streakName || `Week ${weekIndex + 1}`}
              {isFirstWeek && (
                <span className="ml-2 text-[9px] text-cyan-400 border border-cyan-700/40 px-1.5 py-0.5 rounded-full">
                  Day 1
                </span>
              )}
            </div>
            <div className="text-[10px] text-gray-400">{targetDate || "—"}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
          {slot.rewardType !== "None" && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-pink-900/40 text-pink-300 border border-pink-700/30">
              {REWARD_TYPES.find(r => r.id === slot.rewardType)?.icon} {slot.rewardType}
            </span>
          )}
          {slot.dooriEnabled && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-900/40 text-cyan-300 border border-cyan-700/30">
              🏆
            </span>
          )}
          <span className="text-gray-500 text-sm">{open ? "▲" : "▼"}</span>
        </div>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
            <div className="px-4 pb-4 border-t border-gray-700/40 pt-3">
              <RewardSlotEditor slot={slot} onChange={onChange} showDoori={isFirstWeek} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── MilestonePanel ───────────────────────────────────────────────────────────

const MILESTONE_CFG = {
  30: { icon: "🔥", color: "orange", label: "Day 30 Milestone" },
  60: { icon: "💙", color: "blue",   label: "Day 60 Milestone" },
  90: { icon: "💜", color: "purple", label: "Day 90 Milestone" },
};

export const MilestonePanel = ({ day, slot, onChange, targetDate }) => {
  const [open, setOpen] = useState(false);
  const { icon, color, label } = MILESTONE_CFG[day];
  return (
    <div className={`rounded-2xl border-2 border-${color}-700/40 bg-${color}-950/20 overflow-hidden`}>
      <button onClick={() => setOpen(p => !p)}
        className="w-full flex items-center justify-between p-4">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-2xl flex-shrink-0">{icon}</span>
          <div className="text-left min-w-0">
            <div className={`text-sm font-black text-${color}-300`}>{label}</div>
            <div className="text-[10px] text-gray-400 truncate">
              {targetDate || "—"} · {slot.streakName || "No name set"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
          {slot.rewardType !== "None" && (
            <span className={`text-[10px] px-2 py-0.5 rounded-full bg-${color}-900/40 text-${color}-300 border border-${color}-700/30`}>
              {REWARD_TYPES.find(r => r.id === slot.rewardType)?.icon} {slot.rewardType}
            </span>
          )}
          <span className="text-gray-400">{open ? "▲" : "▼"}</span>
        </div>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
            <div className="px-4 pb-4">
              <RewardSlotEditor slot={slot} onChange={onChange} showDoori={false} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── ApprovalCard ─────────────────────────────────────────────────────────────

export const ApprovalCard = ({ request, onApprove, onReject }) => {
  const [done, setDone] = useState(false);
  if (done) return null;
  return (
    <SwipeCard
      onSwipeRight={() => { onApprove(request); setDone(true); }}
      onSwipeLeft={()  => { onReject(request);  setDone(true); }}
      rightLabel="✅ Approve" leftLabel="❌ Reject">
      <div className="bg-gray-900 border border-gray-700 rounded-xl p-4 space-y-2">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-sm font-bold text-white">{request.memberName}</div>
            <div className="text-[10px] text-gray-400">{request.memberCode}</div>
          </div>
          <div className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-900/30 text-yellow-400 border border-yellow-700/30">
            ⏳ Pending
          </div>
        </div>
        <div className="bg-gray-800/60 rounded-xl p-3">
          <div className="text-xs font-bold text-cyan-300">{request.streakTitle}</div>
          <div className="text-[11px] text-gray-300 mt-1">{request.description}</div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-[11px] text-gray-400">
          <div>📅 {new Date(request.date).toLocaleDateString("en-IN")}</div>
          {request.weekNumber != null && <div>🎯 Week {request.weekNumber}</div>}
          {/* <div>🎯 Week {request.weekNumber}</div> */}
        </div>
        {request.proof && <div className="text-[10px] text-blue-400 underline">📎 Proof attached</div>}
      </div>
    </SwipeCard>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// PDMentorShared — main shared engine
// ═══════════════════════════════════════════════════════════════════════════════

export default function PDMentorShared({ config, role }) {
  const {
    userType,
    memberLabel,
    memberSingular,
    memberIcon,
    headerIcon,
    headerTitle,
    headerSub,
    accentColor,   // "pink" | "cyan" | etc — used for border-t color
    targetType,
  } = config;

  const authState = useSelector(s => s.authReducer);
  const navigate = useNavigate();
  const [fetchData] = useFetch();

  const userId   = authState?.user?._id;
  const userCode = authState?.user?.think5whyscode || "";
  const token    = authState?.token;

  // ── Navigation ────────────────────────────────────────────────────
  const tabs = useMemo(() => TABS(config), [config]);
  const [activeTab, setActiveTab] = useState("members");
  const [message,   setMessage]   = useState(null);

  // ── Core selection state ──────────────────────────────────────────
  const [members,        setMembers]        = useState([]);   // [{userId, userCode, userName, isActive, avatar}]
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  const [subs,        setSubs]        = useState([]);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);

  // ── Streak settings & approvals ───────────────────────────────────
  const [streakSettings,   setStreakSettings]   = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);

  // ── Planner form ──────────────────────────────────────────────────
  const [plannerStartDate, setPlannerStartDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [weekRewards, setWeekRewards] = useState(defaultWeekRewards);
  const [milestone30, setMilestone30] = useState(emptySlot("30-Day Milestone"));
  const [milestone60, setMilestone60] = useState(emptySlot("60-Day Milestone"));
  const [milestone90, setMilestone90] = useState(emptySlot("90-Day Milestone"));
  const [aiVerify,    setAiVerify]    = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [plannerView, setPlannerView] = useState("weeks");

  // ── Derived planner values ────────────────────────────────────────
  const weekDates    = useMemo(() => computeWeekDates(plannerStartDate), [plannerStartDate]);
  const weekdayLabel = useMemo(
    () => plannerStartDate ? WEEKDAYS[new Date(plannerStartDate).getDay()] : "",
    [plannerStartDate]
  );
  const milestoneDate = useCallback(n => addDays(plannerStartDate, n), [plannerStartDate]);

  // ── Active sub check ──────────────────────────────────────────────
  const selectedSubActive = isSubActive(selectedSub);

  // ── Calendar date range ───────────────────────────────────────────
  const calStart = streakSettings[0]?.startDate
    ? new Date(streakSettings[0].startDate).toISOString().split("T")[0]
    : selectedSub?.startDate
    ? new Date(selectedSub.startDate).toISOString().split("T")[0]
    : null;

  const calEnd = streakSettings[0]?.endDate
    ? new Date(streakSettings[0].endDate).toISOString().split("T")[0]
    : selectedSub?.endDate
    ? new Date(selectedSub.endDate).toISOString().split("T")[0]
    : null;

  // ─────────────────────────────────────────────────────────────────
  // STEP 1: Load all members linked to this user (by userType)
  // POST /pd/onboarding/linked/light  { userId, userCode, userType }
  // Returns: { status, data: [{userId, userCode, userName, isActive}] }
  // ─────────────────────────────────────────────────────────────────
  const fetchMembers = useCallback(async () => {
    if (!userId || !userCode) return;
    setLoadingMembers(true);
    try {
      const res = await fetchData({
        url: "/pd/onboarding/linked/light",
        method: "post",
        data: { userId, userCode, userType },
        headers: { Authorization: token },
      });
      if (res?.status) {
        setMembers((res.data || []).map(m => ({ ...m, avatar: getAvatar(m.userName || "") })));
      }
    } catch (err) {
      console.error("fetchMembers:", err);
    } finally {
      setLoadingMembers(false);
    }
  }, [userId, userCode, userType, token]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  // ─────────────────────────────────────────────────────────────────
  // STEP 2: When a member is selected, load their subscriptions
  // POST /pd/subscription/linked/light  { userId: member.userId }
  // Returns: { status, data: [{_id, subscriptionId, startDate, endDate, isActive}] }
  // ─────────────────────────────────────────────────────────────────
  const fetchSubsForMember = useCallback(async (memberUserId) => {
    if (!memberUserId) return;
    setSubs([]);
    setSelectedSub(null);
    setStreakSettings([]);
    setLoadingSubs(true);
    try {
      const res = await fetchData({
        url: "/pd/subscription/linked/light",
        method: "post",
        data: { userId: memberUserId },
        headers: { Authorization: token },
      });
      if (res?.status) {
        const list = res.data || [];
        setSubs(list);
        const firstActive = list.find(s => isSubActive(s));
        setSelectedSub(firstActive || list[0] || null);
      }
    } catch (err) {
      console.error("fetchSubsForMember:", err);
    } finally {
      setLoadingSubs(false);
    }
  }, [token]);

  useEffect(() => {
    if (selectedMember?.userId) fetchSubsForMember(selectedMember.userId);
    else { setSubs([]); setSelectedSub(null); setStreakSettings([]); }
  }, [selectedMember?.userId]);

  // ─────────────────────────────────────────────────────────────────
  // STEP 3: When sub is selected, load streak settings
  // ─────────────────────────────────────────────────────────────────
  const fetchSettings = useCallback(async () => {
    if (!userId || !selectedMember?.userId) return;
    try {
      const res = await fetchData({
        url: "/pd/streaks/settings-get",
        method: "post",
        data: {
          ownerId:  userId,
          targetId: selectedMember.userId,
          ...(selectedSub?.subscriptionId ? { subscriptionId: selectedSub.subscriptionId } : {}),
        },
        headers: { Authorization: token },
      });
      if (res?.success) setStreakSettings(res.data || []);
    } catch (err) {
      console.error("fetchSettings:", err);
    }
  }, [userId, selectedMember?.userId, selectedSub?.subscriptionId, token]);

  useEffect(() => {
    if (selectedSub) fetchSettings();
    else setStreakSettings([]);
  }, [selectedSub?.subscriptionId]);

  // Demo approvals — replace with real API
  // useEffect(() => {
  //   if (!selectedMember) return;
  //   setPendingApprovals([
  //     {
  //       _id: "demo1", memberName: selectedMember.userName, memberCode: selectedMember.userCode,
  //       streakTitle: "📚 Week 3 Study Session Complete",
  //       description: "Finished assigned chapters & submitted worksheet.",
  //       date: new Date(), weekNumber: 3, proof: true,
  //     },
  //     {
  //       _id: "demo2", memberName: selectedMember.userName, memberCode: selectedMember.userCode,
  //       streakTitle: "🏃 Week 3 Morning Exercise",
  //       description: "30 minutes of physical activity completed.",
  //       date: new Date(), weekNumber: 3, proof: false,
  //     },
  //   ]);
  // }, [selectedMember?.userId]);

    const fetchPendingApprovals = useCallback(async () => {
    if (!userCode) return;
    try {
      const res = await fetchData({
        url: "/pd/streaks/claim-pending",
        method: "post",
        data: { shopkeeperCode: userCode },
        headers: { Authorization: token },
      });
      if (res?.success) {
        setPendingApprovals(
          (res.data || []).map(c => ({
            _id: c._id,
            memberName: c.customerName,
            memberCode: c.customerCode,
            streakTitle: `${(c.streakDuration || "").replace("D", "")}-Day Streak Milestone`,
            description: `${c.calculatedStreakPoints} pts requested`,
            date: c.billDate,
          }))
        );
      }
    } catch (err) {
      console.error("fetchPendingApprovals:", err);
    }
  }, [userCode, token]);

  useEffect(() => { fetchPendingApprovals(); }, [fetchPendingApprovals]);

  // ── Save planner ──────────────────────────────────────────────────
  const saveSettings = async () => {
    if (!selectedMember || !selectedSub) {
      setMessage({ type: "error", text: `Select a ${memberSingular.toLowerCase()} and active subscription first.` });
      return;
    }
    if (!isSubActive(selectedSub)) {
      setMessage({ type: "error", text: "Streak planning requires an active subscription." });
      return;
    }
    if (!plannerStartDate) {
      setMessage({ type: "error", text: "Choose a start date (Day 1) first." });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetchData({
        url: "/pd/streaks/settings-save",
        method: "post",
        data: {
          ownerId: userId, ownerCode: userCode,
          ownerName: authState?.user?.name || "", ownerType: userType,
          targetId: selectedMember.userId, targetCode: selectedMember.userCode,
          targetName: selectedMember.userName, targetType,
          subscriptionId: selectedSub.subscriptionId,
          startDate: plannerStartDate,
          endDate: addDays(plannerStartDate, 90),
          weeklyStreakDayOfWeek: new Date(plannerStartDate).getDay(),
          weekRewards: weekRewards.map(w => ({ ...w, rewardValue: Number(w.rewardValue) || 0 })),
          milestone30: { ...milestone30, rewardValue: Number(milestone30.rewardValue) || 0 },
          milestone60: { ...milestone60, rewardValue: Number(milestone60.rewardValue) || 0 },
          milestone90: { ...milestone90, rewardValue: Number(milestone90.rewardValue) || 0 },
          aiVerificationRequired: aiVerify,
          targetStreakCount: 12,
        },
        headers: { Authorization: token },
      });
      if (res?.success) {
        setMessage({ type: "success", text: `🎯 12-Week plan saved for ${selectedMember.userName}!` });
        fetchSettings();
      } else {
        setMessage({ type: "warning", text: res?.message || "Save failed." });
      }
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.message || err?.message || "Something went wrong." });
    } finally {
      setSaving(false);
    }
  };

  // const handleApprove = req => {
  //   setPendingApprovals(p => p.filter(r => r._id !== req._id));
  //   setMessage({ type: "success", text: `✅ Approved: ${req.streakTitle}` });
  // };
  // const handleReject = req => {
  //   setPendingApprovals(p => p.filter(r => r._id !== req._id));
  //   setMessage({ type: "warning", text: `❌ Rejected: ${req.streakTitle}` });
  // };
  const handleApprove = async req => {
    try {
      const res = await fetchData({
        url: "/pd/streaks/claim-approve",
        method: "post",
        data: { claimId: req._id, approvedBy: userId, approvalRemarks: `Approved by ${userType.toLowerCase()}` },
        headers: { Authorization: token },
      });
      if (res?.success) {
        setPendingApprovals(p => p.filter(r => r._id !== req._id));
        setMessage({ type: "success", text: `✅ Approved: ${req.streakTitle}` });
      } else {
        setMessage({ type: "warning", text: res?.message || "Approval failed" });
      }
    } catch (err) {
      setMessage({ type: "error", text: "Approval error" });
    }
  };

  const handleReject = async req => {
    try {
      const res = await fetchData({
        url: "/pd/streaks/claim-reject",
        method: "post",
        data: { claimId: req._id, approvedBy: userId, approvalRemarks: `Rejected by ${userType.toLowerCase()}` },
        headers: { Authorization: token },
      });
      if (res?.success) {
        setPendingApprovals(p => p.filter(r => r._id !== req._id));
        setMessage({ type: "warning", text: `❌ Rejected: ${req.streakTitle}` });
      } else {
        setMessage({ type: "warning", text: res?.message || "Rejection failed" });
      }
    } catch (err) {
      setMessage({ type: "error", text: "Rejection error" });
    }
  };

  // ── Stats ─────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    totalMembers: members.length,
    activeSubs:   subs.filter(isSubActive).length,
    pending:      pendingApprovals.length,
    activePlans:  streakSettings.filter(s => s.settingStatus === "Active").length,
  }), [members, subs, pendingApprovals, streakSettings]);

  // ─────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────
  return (
    <div
      className={`space-y-4 bg-gray-900 p-4 rounded-2xl text-white border-t-2 border-${accentColor}-400/40 mt-2 min-h-screen`}
    >
      {/* Header */}
      <div className="flex items-center gap-3">
        <div
          className={`w-10 h-10 rounded-2xl bg-gradient-to-br from-${accentColor}-600 to-${accentColor}-700 flex items-center justify-center text-xl shadow-lg shadow-${accentColor}-900/30`}
        >
          {headerIcon}
        </div>
        <div>
          <div className="font-black text-base text-white">{headerTitle}</div>
          <div className="text-[11px] text-gray-400">{headerSub}</div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-2">
        <StatPill
          icon={memberIcon}
          value={stats.totalMembers}
          label={memberLabel}
          color={`text-${accentColor}-300`}
        />
        <StatPill
          icon="📋"
          value={stats.activeSubs}
          label="Active Subs"
          color="text-cyan-300"
        />
        <StatPill
          icon="⏳"
          value={stats.pending}
          label="Pending"
          color="text-yellow-300"
        />
        <StatPill
          icon="🎯"
          value={stats.activePlans}
          label="Active Plans"
          color="text-green-300"
        />
      </div>

      {/* Message */}
      <AnimatePresence>
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            onClick={() => setMessage(null)}
            className={`p-3 rounded-xl text-xs font-bold cursor-pointer ${
              message.type === "success"
                ? "bg-green-900/40 text-green-300 border border-green-700/30"
                : message.type === "warning"
                  ? "bg-yellow-900/40 text-yellow-300 border border-yellow-700/30"
                  : "bg-red-900/40 text-red-300 border border-red-700/30"
            }`}
          >
            {message.text}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tabs */}
      <div className="grid grid-cols-5 gap-1">
        {tabs.map((t) => (
          <motion.button
            key={t.id}
            whileTap={{ scale: 0.92 }}
            onClick={() => setActiveTab(t.id)}
            className={`py-2 rounded-xl text-center transition-all ${
              activeTab === t.id
                ? `bg-${accentColor}-500 text-black font-black shadow-md`
                : "bg-gray-800/80 text-gray-400 border border-gray-700/60"
            }`}
          >
            <div className="text-base leading-none">{t.icon}</div>
            <div className="text-[9px] font-bold mt-0.5 leading-tight">
              {t.label}
            </div>
          </motion.button>
        ))}
      </div>

      {/* ═══════════════════════════════════════
          TAB: MEMBERS
          Flow: list → tap member → subs appear
                → tap sub → calendar appears
      ═══════════════════════════════════════ */}
      {activeTab === "members" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className={`text-xs font-bold text-${accentColor}-300`}>
              {memberIcon} My {memberLabel}
            </div>
            <div className="text-[10px] text-gray-400">
              {members.length} linked
            </div>
          </div>

          {/* Loading skeletons */}
          {loadingMembers && (
            <div className="space-y-2">
              {[1, 2].map((i) => (
                <div
                  key={i}
                  className="bg-gray-800 rounded-2xl p-4 animate-pulse h-20"
                />
              ))}
            </div>
          )}

          {/* Empty state */}
          {!loadingMembers && members.length === 0 && (
            <div
              className={`bg-gray-900 border border-${accentColor}-500/20 rounded-2xl p-8 text-center space-y-3`}
            >
              <div className="text-4xl">{memberIcon}</div>
              <div className={`text-${accentColor}-300 font-bold text-sm`}>
                No {memberLabel.toLowerCase()} linked yet
              </div>
              <div className="text-gray-400 text-xs">
                {memberLabel} need to be onboarded with your{" "}
                {userType.toLowerCase()} code to appear here.
              </div>
              <div className="text-[11px] font-bold text-white bg-gray-800 border border-gray-700 rounded-xl p-3">
                Your Code:{" "}
                <span className={`text-${accentColor}-400`}>{userCode}</span>
              </div>
            </div>
          )}

          {/* Member list */}
          {members.map((member) => {
            const isSelected =
              String(selectedMember?.userId) === String(member.userId);
            return (
              <div key={String(member.userId)}>
                {/* Member row */}
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedMember(isSelected ? null : member)}
                  className={`w-full text-left rounded-2xl border p-3 transition-all ${
                    isSelected
                      ? `border-${accentColor}-500/60 bg-${accentColor}-950/20 shadow-md shadow-${accentColor}-900/20`
                      : "border-gray-700/60 bg-gray-900/60"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl flex-shrink-0 border-2 ${
                        isSelected
                          ? `border-${accentColor}-400`
                          : "border-gray-700"
                      }`}
                      style={{
                        background: "linear-gradient(135deg,#1e1b4b,#312e81)",
                      }}
                    >
                      {member.avatar}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-white truncate">
                        {member.userName}
                      </div>
                      <div className="text-[11px] text-gray-400">
                        {member.userCode}
                      </div>
                    </div>
                    <div className="text-gray-500 text-sm flex-shrink-0">
                      {isSelected ? "▲" : "▼"}
                    </div>
                  </div>
                </motion.button>

                {/* Expanded panel */}
                <AnimatePresence>
                  {isSelected && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.22 }}
                      className="overflow-hidden"
                    >
                      <div
                        className={`mt-1 ml-3 pl-3 border-l-2 border-${accentColor}-800/40 pb-3 space-y-3`}
                      >
                        <div>
                          <div className="text-xs lg:text-sm text-cyan-200 font-bold uppercase tracking-wide py-2 flex items-center gap-2">
                            Subscriptions
                            {loadingSubs && (
                              <span className="text-gray-600 normal-case font-normal">
                                Loading…
                              </span>
                            )}
                          </div>

                          {!loadingSubs && subs.length === 0 && (
                            <div className="text-[11px] text-gray-500 text-center py-3 border border-dashed border-gray-700 rounded-xl">
                              No subscriptions found for this{" "}
                              {memberSingular.toLowerCase()}.
                            </div>
                          )}

                          <div className="space-y-2">
                            {subs.map((sub) => {
                              const active = isSubActive(sub);
                              const isSel = selectedSub?._id === sub._id;
                              return (
                                <button
                                  key={sub._id || sub.subscriptionId}
                                  onClick={() =>
                                    setSelectedSub(isSel ? null : sub)
                                  }
                                  className={`w-full text-left rounded-xl border p-3 transition-all ${
                                    isSel
                                      ? active
                                        ? "border-cyan-500/60 bg-cyan-950/20"
                                        : "border-gray-500/50 bg-gray-800/60"
                                      : active
                                        ? "border-gray-700/50 bg-gray-800/40 hover:border-cyan-800/50"
                                        : "border-gray-700/30 bg-gray-900/30 opacity-60"
                                  }`}
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <div className="text-[11px] font-bold text-white truncate">
                                        {sub.subscriptionId}
                                      </div>
                                      <div className="text-[9px] text-gray-400 mt-0.5">
                                        {new Date(
                                          sub.startDate,
                                        ).toLocaleDateString("en-IN")}{" "}
                                        →{" "}
                                        {new Date(
                                          sub.endDate,
                                        ).toLocaleDateString("en-IN")}
                                      </div>
                                    </div>
                                    <div className="flex-shrink-0 text-right space-y-1">
                                      <div
                                        className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${
                                          active
                                            ? "bg-green-900/40 text-green-400 border-green-700/30"
                                            : "bg-red-900/30 text-red-400 border-red-700/30"
                                        }`}
                                      >
                                        {active ? "● Active" : "🔒 Expired"}
                                      </div>
                                      {isSel && (
                                        <div className="text-[9px] text-cyan-400 font-bold">
                                          Selected ✓
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  {isSel && !active && (
                                    <div className="mt-2 text-[9px] text-yellow-400 bg-yellow-900/20 border border-yellow-700/30 rounded-lg px-2 py-1.5">
                                      ⚠️ Expired — calendar view only. Planning
                                      requires an active subscription.
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* ── CTA ── */}
                        {selectedSub &&
                          (selectedSubActive ? (
                            <>
                              <button
                                onClick={() => setActiveTab("planner")}
                                className={`w-fit px-2 py-1 rounded-xl bg-gradient-to-r from-${accentColor}-600 to-${accentColor}-700 text-white font-black text-xs shadow-lg active:scale-[0.98] transition-transform`}
                              >
                                📅 Open 12-Week Streak Planner →
                              </button>
                              <button
                                onClick={() => navigate("/purpledoordashboard")}
                                className={`w-fit px-2 py-1 rounded-xl bg-gradient-to-r from-${accentColor}-600 to-${accentColor}-700 text-white font-black text-xs shadow-lg active:scale-[0.98] transition-transform`}
                              >
                                ⚡ View Daily Task →
                              </button>
                              <button
                                onClick={() => setActiveTab("streaksettings")}
                                className={`w-fit px-2 py-1 rounded-xl bg-gradient-to-r from-${accentColor}-600 to-${accentColor}-700 text-white font-black text-xs shadow-lg active:scale-[0.98] transition-transform`}
                              >
                                📅 Streak Settings →
                              </button>
                            </>
                          ) : (
                            <div className="w-full py-2.5 rounded-xl bg-gray-800/60 border border-gray-700/40 text-gray-500 font-bold text-xs text-center">
                              🔒 Active subscription required for planning
                            </div>
                          ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}

      {activeTab === "streaksettings" && (
        <StreakSettingsForm
          targetId={selectedMember?._id}
          targetCode={selectedMember?.think5whyscode}
          targetName={selectedMember?.name}
          targetType={role === "MENTOR" ? "Mentee" : "Child"}
        />
      )}

      {/* ═══════════════════════════════════════
          TAB: PLANNER
      ═══════════════════════════════════════ */}
      {activeTab === "planner" && (
        <div className="space-y-4">
          {/* Context banner */}
          {!selectedMember ? (
            <button
              onClick={() => setActiveTab("members")}
              className={`w-fit px-2 py-1 rounded-xl border-2 border-dashed border-${accentColor}-700/40 text-${accentColor}-400 text-sm font-bold text-center`}
            >
              {memberIcon} Select a {memberSingular} First
            </button>
          ) : !selectedSub ? (
            <button
              onClick={() => setActiveTab("members")}
              className="w-fit px-2 py-1 rounded-xl border-2 border-dashed border-cyan-700/40 text-cyan-400 text-sm font-bold text-center"
            >
              📋 Select a Subscription First
            </button>
          ) : (
            <div
              className={`flex items-center gap-3 rounded-2xl p-3 border ${
                selectedSubActive
                  ? `bg-${accentColor}-950/30 border-${accentColor}-700/30`
                  : "bg-gray-800/60 border-gray-600/40"
              }`}
            >
              <div className="text-2xl">{selectedMember.avatar}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-white truncate">
                  {selectedMember.userName}
                </div>
                <div className="text-[10px] text-gray-400 truncate">
                  {selectedSub.subscriptionId}
                </div>
                <div
                  className={`text-[9px] font-bold mt-0.5 ${selectedSubActive ? "text-green-400" : "text-red-400"}`}
                >
                  {selectedSubActive
                    ? "● Active subscription"
                    : "🔒 Expired subscription"}
                </div>
              </div>
              <button
                onClick={() => setActiveTab("members")}
                className={`text-[10px] text-${accentColor}-400 border border-${accentColor}-700/40 w-fit px-2 py-1 rounded-lg flex-shrink-0`}
              >
                Change
              </button>
            </div>
          )}

          {/* Block if expired */}
          {selectedMember && selectedSub && !selectedSubActive && (
            <div className="bg-red-950/30 border border-red-700/30 rounded-2xl p-5 text-center space-y-2">
              <div className="text-3xl">🔒</div>
              <div className="text-red-300 font-bold text-sm">
                Subscription has expired
              </div>
              <div className="text-gray-400 text-xs">
                Streak planning requires an active subscription.
              </div>
              <button
                onClick={() => setActiveTab("members")}
                className="mt-1 w-fit px-2 py-1 rounded-xl bg-gray-700 text-white text-xs font-bold"
              >
                ← Back to {memberLabel}
              </button>
            </div>
          )}

          {/* Planner content — active sub only */}
          {selectedMember && selectedSubActive && (
            <>
              {/* Day 1 picker */}
              <div className="bg-gray-800/60 border border-gray-700/40 rounded-2xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-xl">📅</span>
                  <div>
                    <div className="text-sm font-black text-white">
                      Choose Day 1
                    </div>
                    <div className="text-[10px] text-gray-400">
                      Weekly checkpoints will fall every{" "}
                      <span className="text-cyan-300 font-bold">
                        {weekdayLabel || "—"}
                      </span>{" "}
                      for 12 weeks.
                    </div>
                  </div>
                </div>
                <input
                  type="date"
                  value={plannerStartDate}
                  onChange={(e) => setPlannerStartDate(e.target.value)}
                  className={inputCls}
                />
                {plannerStartDate && (
                  <div className="grid grid-cols-3 gap-2 text-[10px]">
                    {[30, 60, 90].map((d) => (
                      <div
                        key={d}
                        className="bg-gray-900/60 rounded-xl p-2 text-center"
                      >
                        <div className="text-gray-400">Day {d}</div>
                        <div
                          className={`font-bold ${
                            d === 30
                              ? "text-orange-300"
                              : d === 60
                                ? "text-blue-300"
                                : "text-purple-300"
                          }`}
                        >
                          {milestoneDate(d)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sub-tabs */}
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: "weeks", label: "12 Weekly Streaks", icon: "📆" },
                  { id: "milestones", label: "Day 30 / 60 / 90", icon: "🏆" },
                ].map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setPlannerView(v.id)}
                    className={`w-fit px-2 py-1 rounded-xl text-sm font-bold border transition-all ${
                      plannerView === v.id
                        ? `bg-${accentColor}-500 border-${accentColor}-500 text-black`
                        : "bg-gray-800 border-gray-700 text-gray-300"
                    }`}
                  >
                    {v.icon} {v.label}
                  </button>
                ))}
              </div>

              {/* Weekly panels */}
              {plannerView === "weeks" && (
                <div className="space-y-2">
                  <div className="text-[11px] text-gray-400">
                    Set a name, remarks and reward for each of the 12 weekly
                    checkpoints. Week 1 also lets you associate the DOORI
                    collectible.
                  </div>
                  {weekRewards.map((slot, i) => (
                    <WeekPanel
                      key={i}
                      weekIndex={i}
                      slot={slot}
                      targetDate={weekDates[i] || ""}
                      isFirstWeek={i === 0}
                      onChange={(updated) => {
                        const arr = [...weekRewards];
                        arr[i] = updated;
                        setWeekRewards(arr);
                      }}
                    />
                  ))}
                </div>
              )}

              {/* Milestone panels */}
              {plannerView === "milestones" && (
                <div className="space-y-3">
                  <div className="text-[11px] text-gray-400">
                    Set special rewards for Day 30, Day 60, and Day 90
                    milestones.
                  </div>
                  <MilestonePanel
                    day={30}
                    slot={milestone30}
                    onChange={setMilestone30}
                    targetDate={milestoneDate(30)}
                  />
                  <MilestonePanel
                    day={60}
                    slot={milestone60}
                    onChange={setMilestone60}
                    targetDate={milestoneDate(60)}
                  />
                  <MilestonePanel
                    day={90}
                    slot={milestone90}
                    onChange={setMilestone90}
                    targetDate={milestoneDate(90)}
                  />
                </div>
              )}

              {/* AI Verification */}
              <button
                onClick={() => setAiVerify((p) => !p)}
                className={`w-fit px-2 py-1 rounded-xl flex items-center justify-between border-2 transition-all ${
                  aiVerify
                    ? "border-purple-500/60 bg-purple-950/20"
                    : "border-gray-700/60 bg-gray-900/60"
                }`}
              >
                <div className="text-left flex-1 min-w-0 pr-3">
                  <div
                    className={`text-sm font-bold ${aiVerify ? "text-purple-300" : "text-white"}`}
                  >
                    🤖 Think5Whys AI Verification
                  </div>
                  <div className="text-[10px] text-gray-400 mt-0.5">
                    AI checks progress before each reward is dispatched
                  </div>
                </div>
                <div
                  className={`w-12 h-6 rounded-full border-2 flex items-center flex-shrink-0 transition-all ${
                    aiVerify
                      ? "border-purple-500 bg-purple-900"
                      : "border-gray-600 bg-gray-800"
                  }`}
                >
                  <motion.div
                    animate={{ x: aiVerify ? 24 : 2 }}
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    className="w-4 h-4 rounded-full bg-white shadow"
                  />
                </div>
              </button>

              {/* Save */}
              <button
                onClick={saveSettings}
                disabled={saving}
                className={`w-fit px-2 py-1 rounded-xl bg-gradient-to-r from-${accentColor}-600 to-${accentColor}-700 text-white font-black text-sm disabled:opacity-50 shadow-lg active:scale-[0.98] transition-transform`}
              >
                {saving
                  ? "Saving…"
                  : `🎯 Save 12-Week Plan for ${selectedMember.userName}`}
              </button>

              {/* Saved plans */}
              {streakSettings.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-bold text-gray-400">
                    Saved Plans
                  </div>
                  {streakSettings.map((s) => (
                    <div
                      key={s._id}
                      className="rounded-xl border border-gray-700/50 bg-gray-900/60 p-3 space-y-2"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="text-sm font-bold text-white">
                            📅{" "}
                            {new Date(s.startDate).toLocaleDateString("en-IN")}{" "}
                            → {new Date(s.endDate).toLocaleDateString("en-IN")}
                          </div>
                          <div className="text-[10px] text-gray-400 mt-0.5">
                            Weekly on{" "}
                            {s.weeklyStreakDayOfWeek != null
                              ? WEEKDAYS[s.weeklyStreakDayOfWeek]
                              : "—"}
                          </div>
                        </div>
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                            s.settingStatus === "Active"
                              ? "bg-green-900/40 text-green-400"
                              : "bg-gray-800 text-gray-400"
                          }`}
                        >
                          {s.settingStatus}
                        </span>
                      </div>
                      <ProgressBar
                        current={s.completedWeeks?.length || 0}
                        target={12}
                        color={`bg-${accentColor}-500`}
                      />
                      <div className="grid grid-cols-3 gap-1 text-[9px] text-gray-400">
                        {[
                          {
                            key: "milestone30Reached",
                            label: "🔥 Day 30",
                            cls: "bg-orange-900/30 text-orange-300",
                          },
                          {
                            key: "milestone60Reached",
                            label: "💙 Day 60",
                            cls: "bg-blue-900/30 text-blue-300",
                          },
                          {
                            key: "milestone90Reached",
                            label: "💜 Day 90",
                            cls: "bg-purple-900/30 text-purple-300",
                          },
                        ].map((m) => (
                          <div
                            key={m.key}
                            className={`rounded-lg p-1.5 text-center ${s[m.key] ? m.cls : "bg-gray-800/60"}`}
                          >
                            {m.label} {s[m.key] ? "✓" : ""}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════
          TAB: APPROVALS
      ═══════════════════════════════════════ */}
      {activeTab === "approvals" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold text-yellow-300">
              ⏳ Pending Approvals
            </div>
            <div className="text-[10px] text-gray-400">
              Swipe right ✅ · left ❌
            </div>
          </div>
          {pendingApprovals.length === 0 ? (
            <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8 text-center space-y-2">
              <div className="text-3xl">✅</div>
              <div className="text-gray-400 text-sm">
                All caught up! No pending approvals.
              </div>
            </div>
          ) : (
            pendingApprovals.map((req) => (
              <ApprovalCard
                key={req._id}
                request={req}
                onApprove={handleApprove}
                onReject={handleReject}
              />
            ))
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════
          TAB: PROGRESS
      ═══════════════════════════════════════ */}
      {activeTab === "progress" && (
        <div className="space-y-4">
          <div className={`text-xs font-bold text-${accentColor}-300`}>
            📊 Progress Overview
          </div>

          {!selectedMember ? (
            <button
              onClick={() => setActiveTab("members")}
              className={`w-fit px-2 py-1 rounded-xl border-2 border-dashed border-${accentColor}-700/40 text-${accentColor}-400 text-sm font-bold text-center`}
            >
              {memberIcon} Select a {memberSingular} to View Progress
            </button>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3 bg-gray-800/60 rounded-2xl p-3">
                <div className="text-3xl">{selectedMember.avatar}</div>
                <div>
                  <div className="font-bold text-white">
                    {selectedMember.userName}
                  </div>
                  <div className="text-[10px] text-gray-400">
                    {selectedMember.userCode}
                  </div>
                  {selectedSub && (
                    <div
                      className={`text-[10px] text-${accentColor}-400 mt-0.5`}
                    >
                      {selectedSub.subscriptionId}
                    </div>
                  )}
                </div>
              </div>

              {!selectedSub ? (
                <div className="text-center text-gray-400 text-sm py-4">
                  Select a subscription in the {memberLabel} tab to view
                  progress.
                </div>
              ) : streakSettings.length === 0 ? (
                <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 text-center">
                  <div className="text-gray-400 text-sm">
                    No streak plan set yet.
                  </div>
                  {selectedSubActive && (
                    <button
                      onClick={() => setActiveTab("planner")}
                      className={`mt-3 w-fit px-2 py-1 rounded-xl bg-${accentColor}-500 text-black text-xs font-black`}
                    >
                      Set First Plan →
                    </button>
                  )}
                </div>
              ) : (
                streakSettings.map((s) => {
                  const completed = s.completedWeeks?.length || 0;
                  const pct = Math.round((completed / 12) * 100);
                  return (
                    <div
                      key={s._id}
                      className={`rounded-2xl border border-${accentColor}-700/30 bg-${accentColor}-950/10 p-4 space-y-3`}
                    >
                      <div className="flex justify-between">
                        <div>
                          <div className="font-bold text-white text-sm">
                            📅 12-Week Programme
                          </div>
                          <div className="text-[10px] text-gray-400">
                            {new Date(s.startDate).toLocaleDateString("en-IN")}{" "}
                            → {new Date(s.endDate).toLocaleDateString("en-IN")}
                            {s.weeklyStreakDayOfWeek != null &&
                              ` · Every ${WEEKDAYS[s.weeklyStreakDayOfWeek]}`}
                          </div>
                        </div>
                        <div
                          className={`text-2xl font-black ${pct >= 100 ? "text-green-400" : `text-${accentColor}-400`}`}
                        >
                          {pct}%
                        </div>
                      </div>
                      <ProgressBar
                        current={completed}
                        target={12}
                        color={`bg-${accentColor}-500`}
                      />
                      <div className="grid grid-cols-6 gap-1">
                        {Array.from({ length: 12 }, (_, i) => {
                          const done = (s.completedWeeks || []).includes(i + 1);
                          const curr = s.currentWeek === i + 1;
                          return (
                            <div
                              key={i}
                              className={`rounded-lg py-1.5 text-center text-[9px] font-black border ${
                                done
                                  ? "bg-green-900/40 text-green-400 border-green-700/30"
                                  : curr
                                    ? `bg-${accentColor}-500/30 text-${accentColor}-300 border-${accentColor}-700/40`
                                    : "bg-gray-800/60 text-gray-500 border-gray-700/40"
                              }`}
                            >
                              W{i + 1}
                              {done && <div>✓</div>}
                            </div>
                          );
                        })}
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-[10px]">
                        {[
                          {
                            label: "Day 30",
                            key: "milestone30Reached",
                            color: "orange",
                          },
                          {
                            label: "Day 60",
                            key: "milestone60Reached",
                            color: "blue",
                          },
                          {
                            label: "Day 90",
                            key: "milestone90Reached",
                            color: "purple",
                          },
                        ].map((m) => (
                          <div
                            key={m.label}
                            className={`rounded-xl p-2 text-center border ${
                              s[m.key]
                                ? `bg-${m.color}-900/30 text-${m.color}-300 border-${m.color}-700/30`
                                : "bg-gray-800/60 text-gray-500 border-gray-700/30"
                            }`}
                          >
                            <div className="font-black">{m.label}</div>
                            <div>{s[m.key] ? "🎉 Reached" : "Pending"}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}

              {selectedSub && calStart && calEnd && (
                <StreakCalendar
                  title={`${selectedMember.userName}'s Streak Calendar`}
                  data={[]}
                  startDate={calStart}
                  endDate={calEnd}
                  dateField="streakBankDate"
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════
          TAB: REWARDS
      ═══════════════════════════════════════ */}
      {activeTab === "rewards" && (
        <div className="space-y-3">
          <div className="text-xs font-bold text-purple-300">
            🎁 Reward History
          </div>
          {[
            {
              name: "Demo Member 1",
              reward: "Purplon",
              value: 50,
              status: "Dispatched",
              date: new Date(),
              week: 4,
            },
            {
              name: "Demo Member 2",
              reward: "Ice Cream",
              value: 1,
              status: "Pending",
              date: new Date(),
              week: 2,
            },
          ].map((r, i) => (
            <div
              key={i}
              className="bg-gray-900 border border-gray-700 rounded-xl p-3"
            >
              <div className="flex justify-between items-start">
                <div>
                  <div className="text-sm font-bold text-white">{r.name}</div>
                  <div className="text-[11px] text-gray-400">
                    🎁 {r.reward} · {r.value} · Week {r.week}
                  </div>
                  <div className="text-[10px] text-gray-500">
                    {new Date(r.date).toLocaleDateString("en-IN")}
                  </div>
                </div>
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full border font-bold ${
                    r.status === "Dispatched"
                      ? "bg-green-900/30 text-green-400 border-green-700/30"
                      : "bg-yellow-900/30 text-yellow-400 border-yellow-700/30"
                  }`}
                >
                  {r.status}
                </span>
              </div>
            </div>
          ))}
          <div className="bg-purple-950/30 border border-purple-700/30 rounded-2xl p-4 text-center space-y-2">
            <div className="text-2xl">🤖</div>
            <div className="text-purple-300 font-bold text-sm">
              Think5Whys AI Verification
            </div>
            <div className="text-gray-400 text-xs">
              When enabled, AI verifies streak progress before any reward is
              dispatched.
            </div>
          </div>
          <div className="bg-cyan-950/30 border border-cyan-700/30 rounded-2xl p-4 space-y-2">
            <div className="text-cyan-300 font-bold text-sm">
              🏆 DOORI Collectibles
            </div>
            <div className="text-gray-400 text-xs">
              Members completing a full 12-week streak earn the DOORI
              collectible — a physical symbol of Discipline, Outstanding Results
              and Impact. Enable the DOORI toggle in Week 1 of the streak plan
              to activate this.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
